import React, { createContext, useState, useEffect } from "react";
import { styled, useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import { BottomNavigation, BottomNavigationAction, Paper } from "@mui/material";
import Box from "@mui/material/Box";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import {
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  Assessment as AssessmentIcon,
  Home as HomeIcon,
} from "@mui/icons-material";
import logo from "../../assets/Images/Clanbridge-logo-White.png";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

const themeOverrides = {
  palette: {
    primary: {
      main: "#C026D3",
      light: "#D8B4FE",
    },
    secondary: {
      main: "#F97316",
      light: "#FDBA74",
    },
    background: {
      default: "#FEFCE8",
    },
  },
};

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  background: `linear-gradient(90deg, ${themeOverrides.palette.primary.main}, ${themeOverrides.palette.secondary.main})`,
  boxShadow: `0 4px 12px rgba(0, 0, 0, 0.2)`,
}));

export const NavContext = createContext();

function ServiceProvDrawer({ children }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [navValue, setNavValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [userName, setUserName] = useState("");

  const ResidentName = localStorage.getItem("ResidentName") || "";

  const navItems = [
    {
      label: "Home",
      route: "/serviceprov-portal",
      icon: <HomeIcon />,
    },
    {
      label: "Dashboard",
      route: "/servicepro-dashboard",
      icon: <DashboardIcon />,
    },
    {
      label: "Accept",
      route: "/tasks/accept",
      icon: <AssignmentTurnedInIcon />,
    },
    {
      label: "Status",
      route: "/tasks/status",
      icon: <AssessmentIcon />,
    },
  ];

  useEffect(() => {
    const storedUserName = localStorage.getItem("userName");
    if (storedUserName) {
      setUserName(storedUserName);
    }
  }, []);

  useEffect(() => {
    const currentIndex = navItems.findIndex((item) => item.route === location.pathname);
    if (currentIndex !== -1) {
      setNavValue(currentIndex);
    }
  }, [location.pathname, navItems]);

  const handleProfileClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    sessionStorage.removeItem("firstName");
    navigate("/", { replace: true });
    handleProfileClose();
  };

  const handleMyAccountClick = () => {
    handleProfileClose();
    navigate("/change-serv-prov-password");
  };
  const handleUserProfileClick = ()=>{
    handleProfileClose();
    navigate("/servicepro-profile");
  }

  const handleNavChange = (event, newValue) => {
    setNavValue(newValue);
    navigate(navItems[newValue].route);
  };

  return (
    <NavContext.Provider value={{ setNavValue }}>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <AppBar position="fixed">
          <Toolbar sx={{ backgroundColor: "#635bff" }}>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              <img
                src={logo}
                style={{ height: "45px", margin: "0px 0 0 0px" }}
                alt="logo"
              />
            </Typography>
            <IconButton
              onClick={handleProfileClick}
              size="small"
              sx={{ ml: 2 }}
              aria-controls={Boolean(anchorEl) ? "account-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={Boolean(anchorEl) ? "true" : undefined}
            >
              <Avatar sx={{ width: 32, height: 32 }}>
                {ResidentName ? ResidentName[0].toUpperCase() : "R"}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              id="account-menu"
              open={Boolean(anchorEl)}
              onClose={handleProfileClose}
              onClick={handleProfileClose}
            >
              <MenuItem
                sx={{
                  color: "#635bff",
                  fontSize: "1.1rem",
                  py: 1,
                  minHeight: "32px",
                }}
              >
                Hello! {ResidentName ? ResidentName : "Resident"}
              </MenuItem>
              <MenuItem
                sx={{
                  fontSize: "1.1rem",
                  py: 0.9,
                  minHeight: "32px",
                }}
                onClick={handleUserProfileClick}
              >
                User Profile
              </MenuItem>
              <MenuItem
                sx={{
                  fontSize: "1.1rem",
                  py: 0.9,
                  minHeight: "32px",
                }}
                onClick={handleMyAccountClick}
              >
                Change Password
              </MenuItem>
              <MenuItem
                sx={{
                  fontSize: "1.1rem",
                  py: 0.9,
                  minHeight: "32px",
                }}
                onClick={handleLogout}
              >
                Logout
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <Box component="main" sx={{ flexGrow: 1, p: 0 }}>
          <DrawerHeader />
          {children}
        </Box>

        <Paper
          elevation={4}
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: "#FFFFFF",
            borderTop: "1px solid rgba(0,0,0,0.1)",
          }}
        >
          <BottomNavigation
            value={navValue}
            onChange={handleNavChange}
            showLabels
            sx={{
              bgcolor: "transparent",
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            {navItems.map((item, index) => (
              <BottomNavigationAction
                key={index}
                label={item.label}
                icon={item.icon}
                sx={{
                  minWidth: 0,
                  px: 1,
                }}
              />
            ))}
          </BottomNavigation>
        </Paper>
      </Box>
    </NavContext.Provider>
  );
}

export default ServiceProvDrawer;